import { z } from 'zod';
import {
  appId,
  appName,
  appText,
  declarationList,
  inlineJsonSchema,
  sourceDeclarationSchema,
} from './app-v2-primitives.mjs';
import {
  collectionDeclarationSchema,
  validateCollectionReferences,
} from './app-v2-collections.mjs';
import { validateSavedSeriesJobReferences } from '../apps/declarations/series-job-input.mjs';
import { validateMediaSourceReferences } from './app-media.mjs';
import { publicDeclarationSchema, validatePublicReferences } from './app-public.mjs';
import {
  trustedRefreshTargetSchema,
  validateTrustedRefreshReferences,
} from './app-trusted-refresh.mjs';
export { trustedRefreshTargetSchema } from './app-trusted-refresh.mjs';

export { appMediaDeclarationSchema } from './app-media.mjs';
export {
  mediaPathSchema,
  mediaSourceRecipeSchema,
  parseMediaPath,
  parseMediaSourceRecipe,
} from './app-media-source.mjs';

export { fieldDeclarationSchema, collectionDeclarationSchema } from './app-v2-collections.mjs';
export {
  sourceBindingDeclarationSchema,
  sourceDeclarationSchema,
  inlineJsonSchema,
} from './app-v2-primitives.mjs';

const named = { id: appId, name: appName };
export const functionDeclarationSchema = z
  .object({
    ...named,
    entry: appText(200),
    inputSchema: inlineJsonSchema,
    outputSchema: inlineJsonSchema,
    operations: z.array(appText(128)).max(100),
  })
  .strict();
const jobFields = {
  ...named,
  schedule: z.object({ expression: appText(200), timeZone: appText(100) }).strict(),
};
export const jobDeclarationSchema = z.union([
  z.strictObject({
    ...jobFields,
    functionId: appId,
    input: z.json(),
    schedule: jobFields.schedule.optional(),
  }),
  z.strictObject({ ...jobFields, target: trustedRefreshTargetSchema, input: z.strictObject({}) }),
]);
export const aiDeclarationSchema = z
  .object({
    ...named,
    prompt: appText(32000),
    outputSchema: inlineJsonSchema.optional(),
    skillRefs: z.array(appId).max(100).default([]),
  })
  .strict();
const tool = z
  .object({
    ...named,
    toolRef: appText(200),
    sourceId: appId.optional(),
    argumentTemplate: z.json().default({}),
    argumentsSchema: inlineJsonSchema,
  })
  .strict();
const series = z
  .object({
    ...named,
    sourceId: appId,
    timeFieldId: appId,
    valueFieldIds: z.array(appId).min(1).max(100),
  })
  .strict();
// What starts a function besides a schedule: an inbound webhook (its own URL
// and HMAC secret; no source needed) or a change to one of the App's own
// collections (`sourceId` names the collection; `operations` narrows to
// insert, update or delete, all three when absent).
const event = z
  .object({
    ...named,
    kind: z.enum(['collection', 'webhook']),
    functionId: appId,
    sourceId: appId.optional(),
    operations: z
      .array(z.enum(['insert', 'update', 'delete']))
      .min(1)
      .max(3)
      .refine((items) => new Set(items).size === items.length)
      .optional(),
  })
  .strict();
const permission = z.enum([
  'camera',
  'clipboard-read',
  'clipboard-write',
  'microphone',
  'geolocation',
]);

// One app.json contract. ART owns the enclosing Artifact/version/source envelope.
// No rows, grants, credentials, publication pointer or active jobs live here.
export const appManifestSchema = z
  .object({
    schemaVersion: z.literal(2),
    title: appText(300),
    description: appText(2000).optional(),
    collections: declarationList(collectionDeclarationSchema).default([]),
    sources: declarationList(sourceDeclarationSchema).default([]),
    series: declarationList(series).default([]),
    tools: declarationList(tool).default([]),
    roles: z
      .array(appName)
      .max(100)
      .refine((items) => new Set(items).size === items.length)
      .default([]),
    functions: declarationList(functionDeclarationSchema).default([]),
    // The CPU, memory and time class every server function call of this App
    // runs with (apps/functions/limits.mjs); `standard` when absent.
    functionClass: z.enum(['small', 'standard', 'large']).optional(),
    // The outside hosts server functions may reach (apps/egress). `read` is
    // GET and HEAD; `read_send` also sends data (POST, PUT, PATCH, DELETE).
    // Declaring is not approval: the App's owner approves each host once.
    network: z
      .object({
        hosts: z
          .array(
            z
              .object({
                host: z
                  .string()
                  .max(253)
                  .regex(/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/),
                access: z.enum(['read', 'read_send']),
              })
              .strict(),
          )
          .max(50)
          .refine((items) => new Set(items.map((item) => item.host)).size === items.length),
      })
      .strict()
      .optional(),
    jobs: declarationList(jobDeclarationSchema).default([]),
    events: declarationList(event).default([]),
    ai: declarationList(aiDeclarationSchema).default([]),
    files: z
      .object({
        mimeTypes: z.array(appText(100)).max(100),
        maxBytes: z.number().int().positive().safe(),
      })
      .strict()
      .optional(),
    notifications: z.array(appId).max(100).default([]),
    approvalPolicyRefs: z.array(appId).max(100).default([]),
    devices: z
      .array(permission)
      .max(5)
      .refine((items) => new Set(items).size === items.length)
      .default([]),
    realtime: z.array(appId).max(100).default([]),
    // What a published copy shows anonymous visitors and the writes they may
    // make (contracts/app-public.mjs). Declaring publishes nothing.
    public: publicDeclarationSchema.optional(),
  })
  .strict()
  .superRefine((manifest, ctx) => {
    validateSavedSeriesJobReferences(manifest, ctx);
    validatePublicReferences(manifest, ctx);
    validateCollectionReferences(manifest.collections, ctx);
    validateMediaSourceReferences(manifest, ctx);
    validateTrustedRefreshReferences(manifest, ctx);
    const functions = new Set(manifest.functions.map((item) => item.id));
    for (const key of ['jobs', 'events'])
      manifest[key].forEach((item, index) => {
        if (!item.target && !functions.has(item.functionId))
          ctx.addIssue({
            code: 'custom',
            path: [key, index, 'functionId'],
            message: 'Reference a declared function',
          });
      });
    const sourceIds = new Set(manifest.sources.map((item) => item.id));
    manifest.tools.forEach((item, index) => {
      if (item.sourceId && !sourceIds.has(item.sourceId))
        ctx.addIssue({
          code: 'custom',
          path: ['tools', index, 'sourceId'],
          message: 'Reference a declared source for owner/viewer binding',
        });
    });
    manifest.series.forEach((item, index) => {
      if (!sourceIds.has(item.sourceId))
        ctx.addIssue({
          code: 'custom',
          path: ['series', index, 'sourceId'],
          message: 'Reference a declared source',
        });
    });
    const collections = new Set(manifest.collections.map((item) => item.id));
    manifest.events.forEach((item, index) => {
      const problem =
        item.kind === 'collection'
          ? !collections.has(item.sourceId) && 'Reference the declared collection'
          : item.operations
            ? 'Only a collection event names operations'
            : item.sourceId !== undefined &&
              !sourceIds.has(item.sourceId) &&
              'Reference a declared source, or leave a webhook without one';
      if (problem)
        ctx.addIssue({ code: 'custom', path: ['events', index, 'sourceId'], message: problem });
    });
    if (manifest.realtime.some((id) => !collections.has(id)))
      ctx.addIssue({
        code: 'custom',
        path: ['realtime'],
        message: 'Reference declared collections',
      });
  });

export const parseAppManifest = (value) => appManifestSchema.parse(value);
export const appManifestJsonSchema = z.toJSONSchema(appManifestSchema, { io: 'input' });

export const appManifestExample = Object.freeze({
  schemaVersion: 2,
  title: 'Parcel register',
  collections: [
    {
      id: 'parcels',
      name: 'parcels',
      fields: [
        { id: 'tracking', name: 'tracking', type: 'text', required: true },
        { id: 'arrived', name: 'arrived', type: 'timestamp', nullable: true },
      ],
    },
  ],
  roles: ['operator'],
});
